import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import FormDatePicker from '@/shared/FormCreator/FormItem/FormDatePicker'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import CourseDialog from '@/components/CourseDialog'
import EmailDialog from '@/components/EmailDialog'
import FormSection from '@/components/FormSection'
import combineDateTime from '@/libs/utils/combineDateTime'
import FormTimePicker from '@/shared/FormCreator/FormItem/FormTimePicker'

import { ReactComponent as DeleteIcon } from '@/assets/trash_bin.svg'

const mapStateToProps = ({ campaign }) => ({ options: campaign.createCampaignOptions })

const inputConfig = { inputBaseProps: { flexDirection: 'column', alignItems: 'flex-start' } }

const PhishCampaignSmsCreateFormFollowUp = ({ dispatch, form, onSubmit, options }) => {
  const { t } = useTranslation()

  const [preview, setPreview] = useState({})
  const [openCourse, setOpenCourse] = useState(false)

  const { completionMail, remindMail, trainingMail, coursePrograms = [] } = options || {}

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
  } = form

  const isStartTimeFixed = isEqual(watch('dateRangeType'), '1')
  const isReminderNotifyChecked = watch('reminderNotify')
  const selectedCourseId = watch('courseId')

  const reminderScheduleAddHandler = () => {
    const list = getValues('reminderScheduleList')
    setValue('reminderScheduleList', [
      ...list,
      moment().set({ second: 0, millisecond: 0 }).toDate(),
    ])
    trigger?.()
  }

  const updateReminderScheduleAt = (value, index) => {
    const list = getValues('reminderScheduleList')

    if (index < list.length) {
      list[index] = value
      setValue('reminderScheduleList', [...list])
      trigger?.()
    }
  }

  const removeReminderScheduleAt = index => {
    const list = getValues('reminderScheduleList')

    if (index < list.length) {
      list.splice(index, 1)
      setValue('reminderScheduleList', [...list])
      trigger?.()
    }
  }

  const scheduleComponent = ({ value, index }) => (
    <Box display="flex" columnGap={1}>
      <IconButton
        onClick={() => {
          removeReminderScheduleAt(index)
        }}
      >
        <DeleteIcon />
      </IconButton>
      <FormDatePicker
        formProps={{
          field: {
            value,
            onChange: val => {
              const newDate = moment(val)
              updateReminderScheduleAt(
                moment(value)
                  .set({ year: newDate.year(), month: newDate.month(), date: newDate.date() })
                  .toDate(),
                index,
              )
            },
          },
        }}
        inputConfig={{ sx: { width: 180 } }}
      />
      <FormTimePicker
        formProps={{
          field: {
            value,
            onChange: val => {
              const newDate = moment(val)
              updateReminderScheduleAt(
                moment(value).set({ hour: newDate.hour(), minute: newDate.minute() }).toDate(),
                index,
              )
            },
          },
        }}
        inputConfig={{ sx: { width: 180, ml: 1 } }}
      />
    </Box>
  )

  useEffect(() => {
    if (isStartTimeFixed) {
      setValue?.('startDate', new Date())
      setValue?.('startTime', new Date())
    }
  }, [isStartTimeFixed, setValue])

  useEffect(() => {
    dispatch({ type: 'campaign/getCampaignOptions' })
  }, [dispatch])

  return (
    <Container maxWidth={false}>
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit, () => {
            trigger?.()
          })}
        >
          <FormSection title={t('general.basic_info')}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.campaign_name')}
                  defaultValue={`${getValues('name')}_${moment().format('YYYYMMDD')}`}
                  name="courseName"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{t('general.select_group')}</Typography>
                <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                  <CheckCircleIcon fontSize="12px" />
                  <Typography variant="caption">{t('messages.followup_group')}</Typography>
                </Stack>
              </Grid>
              <Grid item container xs={12} sm={6} spacing={1}>
                <Grid item>
                  <FormItem
                    label={t('general.select_course')}
                    defaultValue=""
                    name="courseId"
                    type="select"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                      options: coursePrograms.map(cp => ({ value: cp.programId, label: cp.name })),
                    }}
                    errors={errors}
                  />
                </Grid>

                <Grid item>
                  <Button
                    disabled={!selectedCourseId}
                    sx={{ mt: { xs: 0, sm: '65%' } }}
                    color="white"
                    onClick={() => {
                      setOpenCourse(true)
                    }}
                  >
                    {t('general.edit_preview')}
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography>{t('general.set_schedule')}</Typography>
                <Stack spacing={1} mt={1}>
                  <Typography>{`${t('general.start_time')}: ${moment(
                    combineDateTime(getValues('startDate'), getValues('startTime')),
                  ).format('YYYY-MM-DD, HH:mm')}`}</Typography>
                  <Typography>{`${t('general.start_time')}: ${moment(
                    combineDateTime(getValues('endDate'), getValues('endTime')),
                  ).format('YYYY-MM-DD, HH:mm')}`}</Typography>
                </Stack>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title={t('general.mail_notify')}>
            <Grid container spacing={2}>
              <Grid item container xs={12} sm={6}>
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography>{`*${t('general.training_invite')}`}</Typography>
                    </Grid>

                    <Grid item container>
                      <Grid item>
                        <FormItem
                          label={t('general.send_training_invite')}
                          defaultValue={true}
                          name="sendInvite"
                          type="checkbox"
                          control={control}
                          inputConfig={inputConfig}
                          errors={errors}
                          disabled={true}
                        />
                      </Grid>

                      <Grid item>
                        <Button
                          color="white"
                          onClick={() => {
                            setPreview({
                              details: trainingMail,
                              title: t('general.training_invite_preview'),
                            })
                          }}
                        >
                          {t('general.preview_template')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item container xs={12} sm={6} spacing={1}>
                <Grid item xs={12}>
                  <Typography>{`*${t('general.notify_email')}`}</Typography>
                </Grid>
                <Grid item container alignItems="center" wrap="nowrap" spacing={1}>
                  <Grid item container alignItems="center" sx={{ width: 'auto' }}>
                    <Grid item>
                      <FormItem
                        label={t('general.training_reminder_notify')}
                        defaultValue={false}
                        name="reminderNotify"
                        type="checkbox"
                        control={control}
                        inputConfig={inputConfig}
                        errors={errors}
                      />
                    </Grid>
                    <Grid item>
                      <Button
                        color="white"
                        onClick={() => {
                          setPreview({
                            details: remindMail,
                            title: t('general.training_reminder_notify_preview'),
                          })
                        }}
                      >
                        {t('general.preview_template')}
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton
                      disabled={
                        !isReminderNotifyChecked || getValues('reminderScheduleList')?.length >= 3
                      }
                      onClick={reminderScheduleAddHandler}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>
                {isReminderNotifyChecked && (
                  <FormItem
                    defaultValue={[]}
                    name="reminderScheduleList"
                    type="controlList"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                      component: scheduleComponent,
                    }}
                    errors={errors}
                  />
                )}
                <Grid item container>
                  <Grid item>
                    <FormItem
                      label={t('general.training_complete_notify')}
                      defaultValue={false}
                      name="completeNotify"
                      type="checkbox"
                      control={control}
                      inputConfig={inputConfig}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item>
                    <Grid item>
                      <Button
                        color="white"
                        onClick={() => {
                          setPreview({
                            details: completionMail,
                            title: t('general.training_complete_notify_preview'),
                          })
                        }}
                      >
                        {t('general.preview_template')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormSection>

          <Button sx={{ float: 'right', m: 2 }} type="submit">
            {t('general.create_campaign')}
          </Button>
        </Box>
      </Card>

      <EmailDialog
        open={!!preview.details}
        details={preview.details}
        title={preview.title}
        onClose={() => setPreview({})}
      />

      <CourseDialog
        open={openCourse}
        courseId={selectedCourseId}
        title={coursePrograms.find(c => c.programId === selectedCourseId)?.name}
        onClose={() => setOpenCourse(false)}
      />
    </Container>
  )
}

export default connect(mapStateToProps)(PhishCampaignSmsCreateFormFollowUp)
